import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface FundInfo {
  name: string;
  bank: string;
  cert_name: string;
  price: number;
  value: number;
  monthly_income: number;
  party_wallet: number;
  subsidy: number;
}

interface Purchase {
  id: number;
  fund_type: string;
  cert_number: string;
  status: string;
  created_at: string;
  fund_info: FundInfo;
  redeemed: boolean;
}

interface UserInfo {
  name: string;
  id_number: string;
}

function maskIdNumber(id: string): string {
  if (!id || id.length < 6) return id;
  return id.substring(0, 6) + '******' + id.substring(id.length - 4);
}

function getGenderFromId(id: string): string {
  if (!id || id.length < 2) return '—';
  const secondToLast = parseInt(id.charAt(id.length - 2), 10);
  if (isNaN(secondToLast)) return '—';
  return secondToLast % 2 === 1 ? '男' : '女';
}

function formatValue(v: number): string {
  return v.toLocaleString('en-US');
}

export default function FundReceipt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasCardFundPromo, setHasCardFundPromo] = useState(false);
  const [fundRedeemOpen, setFundRedeemOpen] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    api.get('/fund-purchases/receipts').then(({ data }) => {
      const list: Purchase[] = data.purchases ?? [];
      setPurchases(list);
      setUser(data.user ?? null);
      setHasCardFundPromo(!!data.has_card_fund_promo);
      // setFundRedeemOpen(true);
      // setFundRedeemOpen(!!data.fund_redeem_open);
      const targetId = searchParams.get('id');
      if (targetId) {
        const idx = list.findIndex(p => String(p.id) === targetId);
        if (idx >= 0) setCurrentIndex(idx);
      }
    }).catch(() => toast.error('加载失败')).finally(() => setLoading(false));
  }, []);

  const current = purchases[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < purchases.length - 1;

  if (loading) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex items-center justify-center" style={{ backgroundColor: '#5a0000' }}>
        <AuthGuard />
        <p className="text-white/60">加载中...</p>
      </div>
    );
  }

  if (purchases.length === 0) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#5a0000' }}>
        <AuthGuard />
        <p className="text-white/60 text-base mb-4">暂无购买记录</p>
        <button
          onClick={() => navigate('/fund')}
          className="bg-[#ffd700] text-[#8b0000] font-bold text-sm px-6 py-2 rounded-lg"
        >
          去购买
        </button>
      </div>
    );
  }

  return (
    <div
      className="max-w-md mx-auto min-h-screen pb-6 relative"
      style={{
        backgroundColor: '#5a0000',
      }}
    >
      <AuthGuard />

      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      {purchases.length > 1 && (
        <>
          {hasPrev && (
            <button
              onClick={() => setCurrentIndex(i => i - 1)}
              className="fixed left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
          )}
          {hasNext && (
            <button
              onClick={() => setCurrentIndex(i => i + 1)}
              className="fixed right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          )}
        </>
      )}

      <img src="/images/jijin_receipt_beijing_1.png" alt="" className="w-full" />
      <div className="px-[40px] bg-[url(/images/jijin_receipt_beijing_2.png)] bg-[length:100%_100%] bg-center bg-no-repeat">
        <div className="text-[#8b0000] text-sm leading-relaxed mb-4">
          <p className="font-bold text-base mb-2">
            <span className="text-[#c4161c]">恭喜！{user?.name ?? '—'}同志：</span>
          </p>
          <p className="leading-relaxed text-[13px]">
            经本人申请、组织审核及合作银行联合确认，您已成功获得[{current?.fund_info?.cert_name}]正式成为本计划的关爱对象，凭此证书可在本行兑现证书相对应价值现金！
          </p>
        </div>

        <div className="relative mb-4">
          <img src="/images/jijin_receipt_bianhao.png" alt="" className="w-[65%] mx-auto" />
          <p className="absolute inset-0 flex items-center justify-center text-[#8b0000] font-bold text-xs tracking-wider">
            证书编号：{current?.cert_number}
          </p>
        </div>

        <div className="relative mb-4 bg-[url(/images/jijin_receipt_xinxi.png)] bg-[length:100%_100%] bg-center bg-no-repeat">
          <div className="relative pt-[20px] pb-[10px] px-[30px]">
            <div className="space-y-0 text-[12px] text-[#333]">
              <div className="flex">
                <span className="w-20 flex-shrink-0 tracking-[0.5em]">姓  名</span>
                <span>{user?.name ?? '—'}</span>
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0 tracking-[0.5em]">性  别</span>
                <span>{user?.id_number ? getGenderFromId(user.id_number) : '—'}</span>
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">政治面貌</span>
                <span>中共党员</span>
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">所在党支部</span>
                <span>中共中央党支部</span>
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">身份证号</span>
                <span>{user?.id_number ? maskIdNumber(user.id_number) : '—'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mb-4 w-[330px] mx-auto">
          <img src="/images/jijin_receipt_xiangqing.png" alt="" className="w-full" />
          <div className="absolute inset-0 px-[6%] top-[35px]">
            <span className="pl-[45px] text-[#ffd700] text-[14px]">{current?.fund_info.name}</span>
            <div className="pt-[10px] pl-[70px] text-white/80 text-[11px]">
              {current?.created_at}
            </div>
            <div className="pt-[10px] pl-[10px] text-[#ffd700] text-[11px]">
              权益价值
            </div>
            <p className="pl-[35px] text-[#ffd700]" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              <span className="text-[16px] mr-2">¥</span><span className="text-[26px] font-bold">{current?.fund_info ? formatValue(current.fund_info.value) : '—'}</span>
            </p>
          </div>
        </div>

        <div className="mb-6">
          <img src="/images/jijin_receipt_wenxin.png" alt="" className="w-full" />
        </div>

        {purchases.length > 1 && (
          <p className="text-center text-[#8b0000] text-xs mb-4">
            第 {currentIndex + 1} / {purchases.length} 份证书
          </p>
        )}

        {/* redeems */}
        {current?.redeemed ? (
          <div className="flex justify-center pb-3">
            <span className="border border-[#8b0000] px-[10px] py-[3px] w-[180px] text-center text-[#8b0000] text-sm font-bold mx-auto rounded-full">该基金已兑现</span>
          </div>
        ) : (
          <button
            onClick={async () => {
              if (!fundRedeemOpen) {
                if (hasCardFundPromo) {
                  toast('五个工作日后统一开放', { icon: '⏳' });
                } else {
                  toast('等待统一开放兑现', { icon: '⏳' });
                }
                return;
              }
              if (!hasCardFundPromo) {
                toast('五个工作日后统一开放', { icon: '⏳' });
                return;
              }
              if (redeeming) return;
              setRedeeming(true);
              try {
                const { data } = await api.post('/fund-purchases/redeem', { purchase_id: current.id });
                toast.success(data?.message ?? '兑现成功');
                setPurchases(prev => prev.map(p => p.id === current.id ? { ...p, redeemed: true } : p));
              } catch (err: any) {
                toast.error(err?.response?.data?.message ?? '兑现失败');
              } finally {
                setRedeeming(false);
              }
            }}
            disabled={redeeming}
            className="block mx-auto disabled:opacity-60"
          >
            <img
              src="/images/jijin_receipt_liji.png"
              alt="立即兑现"
              className="h-11 mx-auto"
            />
          </button>
        )}
      </div>
      <img src="/images/jijin_receipt_beijing_3.png" alt="" />
    </div>
  );
}
